// ─────────────────────────────────────────────────────────────────────────────
// AudioIOCard — audio I/O meters for the desktop homepage right panel (Chunk 5).
//
// INPUT: live PCM bar waveform + level from the client mic meter (useMicMeter). The
// single useMicMeter instance lives in RexyHomepage (it also feeds the orb's
// userAmplitude) and its MicMeter result is passed in here — so the mic is opened
// once, never twice.
// OUTPUT: placeholder. Desktop has no client-side TTS envelope metering (mirrors
// iOS, where agent audio is a proxy oscillator, not real decode) and there is no
// rexy:amplitude backend event — so the output row renders a flat N/A meter.
//
// Live only during an active session: meter.active gates the bars. When idle the
// meter is torn down (bars all 0) and the card shows a flat IDLE state.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import { MicMeter } from '../hooks/usePTT';
import VoiceEngineToggle from './VoiceEngineToggle';
import AudioInputPrefs from './AudioInputPrefs';

const MONO = REXY_FONT;
const OUTPUT_BARS = 16; // flat placeholder row, matched to usePTT's 16-bar input contract (iOS parity)

function BarMeter({ bars, color }: { bars: number[]; color: string }) {
  return (
    <div style={styles.bars}>
      {bars.map((b, i) => (
        <div
          key={i}
          style={{
            ...styles.bar,
            height: `${Math.max(2, b * 100)}%`,
            backgroundColor: color,
          }}
        />
      ))}
    </div>
  );
}

export interface AudioIOCardProps {
  meter: MicMeter;
}

export default function AudioIOCard({ meter }: AudioIOCardProps) {
  const { level, bars, active } = meter;
  // When inactive the bars are already zeroed by the hook; render the array as-is.
  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>║ AUDIO I/O</div>

      {/* INPUT — live mic */}
      <div style={styles.meterBlock}>
        <div style={styles.rowTop}>
          <span style={styles.label}>INPUT · MIC</span>
          <span style={{ ...styles.statusDot, color: active ? REXY_CONSOLE.primary : REXY_CONSOLE.dimText }}>
            {active ? '● LIVE' : '○ IDLE'}
          </span>
        </div>
        <BarMeter bars={bars} color={REXY_CONSOLE.primary} />
        <div style={styles.sub}>{active ? `LEVEL ${Math.round(level * 100)}%` : 'LEVEL — N/A'}</div>
      </div>

      {/* OUTPUT — placeholder (no client TTS metering) */}
      <div style={styles.meterBlock}>
        <div style={styles.rowTop}>
          <span style={styles.label}>OUTPUT · TTS</span>
          <span style={{ ...styles.statusDot, color: REXY_CONSOLE.dimText }}>○ N/A</span>
        </div>
        <BarMeter bars={new Array(OUTPUT_BARS).fill(0)} color={REXY_CONSOLE.dimText} />
        <div style={styles.sub}>NO CLIENT ENVELOPE</div>
      </div>

      {/* VOICE ENGINE — local-primary / Deepgram-override axis (Phase 4 D). Own axis,
          NOT the F1/D4 reasoning routing_mode. Surfaces the manual force-Deepgram override. */}
      <VoiceEngineToggle />

      {/* AUDIO INPUT — Phase 5 noise suppression (DeepFilterNet) + VAD sensitivity scale. */}
      <AudioInputPrefs />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 12,
  },
  meterBlock: {
    marginBottom: 12,
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  label: {
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.primary,
  },
  statusDot: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
  },
  bars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 2,
    height: 28,
    background: REXY_CONSOLE.panel,
    borderRadius: 3,
    padding: '2px 4px',
    overflow: 'hidden',
  },
  bar: {
    flex: 1,
    minWidth: 2,
    borderRadius: 1,
  },
  sub: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.dimText,
    marginTop: 4,
  },
};
